<template>
  <div class="horizontal-slider" data-cy="horizontal-slider">
    <div class="navigation-button-wrapper">
      <b-button
        @click="slideLeft"
        :class="{'is-visible' : slidePosition > 0}"
        icon-pack="fas"
        icon-left="chevron-left"
        class="navigation-button"
      />
    </div>
    <ul v-touch:swipe="onSwipe" class="horizontal-slider-list">
      <slot />
    </ul>
    <div>
      <b-button
        @click="slideRight"
        :class="{'is-visible': slidePosition < totalItems - pageSize}"
        icon-pack="fas"
        icon-right="chevron-right"
        class="navigation-button"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'HorizontalSlider',
  data () {
    return {
      dom: {
        list: null,
        items: []
      },
      observer: {
        mutation: null,
        resize: null
      },
      slidePosition: 0,
      pageSize: 0,
      pageCount: 0,
      totalItems: 0
    }
  },
  mounted () {
    this.setObservers()
  },
  beforeDestroy () {
    // Clean up
    this.disconnectObservers()
  },
  methods: {
    // Lifecycle
    setObservers () {
      this.observer.resize = new ResizeObserver(this.refreshDomElements)
      this.observer.resize.observe(this.$el)

      this.observer.mutation = new MutationObserver(this.refreshDomElements)
      this.observer.mutation.observe(this.$el.querySelector('.horizontal-slider-list'), { attributes: true, childList: true, characterData: true, subtree: true })
    },

    disconnectObservers () {
      this.observer.resize.disconnect()
      this.observer.mutation.disconnect()
    },

    refreshDomElements () {
      this.dom.list = this.$el?.querySelector('.horizontal-slider-list')
      this.dom.items = this.dom.list?.children || []

      this.pageSize = Math.floor(this.dom.items.length ? this.dom.list.clientWidth / this.dom.items[0].clientWidth : 0)
      this.pageCount = Math.floor(this.dom.items.length / this.pageSize)

      this.totalItems = this.dom.items.length
      this.slidePosition = 0

      this.updateScroll()
    },

    // Navigation
    onSwipe (dir) {
      switch (dir) {
        case 'left':
          this.slideRight()
          break
        case 'right':
          this.slideLeft()
          break
        default:
          break
      }
    },
    slideLeft () {
      this.slidePosition = Math.max(0, this.slidePosition - this.pageSize)
      this.updateScroll()
    },
    slideRight () {
      this.slidePosition = Math.min(this.totalItems - this.pageSize, this.slidePosition + this.pageSize)
      this.updateScroll()
    },
    updateScroll () {
      const scrollToIndex = this.slidePosition
      this.dom.list.scrollTo({
        left: this.dom.list.children[scrollToIndex].offsetLeft - this.dom.list.offsetLeft,
        behavior: 'smooth'
      })
    }
  }
}
</script>

<style scoped lang="scss">
@import "/assets/css/components";

$spacing: 15px;
$padding: 10px;
$vertical-padding: 20px;
$slides-per-page: (
  "xs": 2,
  "sm": 3,
  "md": 4,
  "lg": 5,
  "xl": 6
);

@mixin slide-width($size) {
  $num-slides: map-get($slides-per-page, $size);
  > * {
    width: calc(calc(1 / $num-slides) * 100% - $spacing + calc(($spacing + $padding) / $num-slides));
  }
}

.horizontal-slider {
  // background-color: white;
  // background-color: #f8f9fc;
  //background-color: rgba(70,125,180,.05);
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7.5px;
  padding: $vertical-padding 30px;
  @media (min-width: 1024px) {
    gap: 15px;
    padding: $vertical-padding 10px;
  }
  @media (min-width: 1216px) {
    padding: $vertical-padding 10px;
  }
}

.horizontal-slider-list {
  overflow: hidden;
  user-select: none;
  display: flex;
  gap: $spacing;
  align-items: stretch;
  padding: 5px 0px 5px 10px;

  > * {
    flex-grow: 1;
    flex-shrink: 0;
  }

  @include slide-width("xs");

  @media (min-width: 680px) {
    @include slide-width("sm");
  }

  @media (min-width: 810px) {
    @include slide-width("md");
  }

  @media (min-width: 1024px) {
    max-width: calc(960px - $spacing * 2);
  }

  @media (min-width: 1216px) {
    max-width: calc(1152px - $spacing * 2);
    @include slide-width("lg");
  }

  @media (min-width: 1408px) {
    max-width: calc(1344px - $spacing * 2);
    @include slide-width("xl");
  }
}

.navigation-button {
  cursor: pointer;
  background: $white;
  border-radius: 9999px;
  z-index: 10;
  transition: all 0.2s ease;
  opacity: 0;

  &.is-visible {
    opacity: 1;
  }

  &:not(.is-visible) {
    pointer-events: none;
    cursor: default;
  }
}
</style>
